import Axios from 'axios';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {herokuUrl, joinUsFormId, portalId} from '../../../api';
import {Button} from '../../../components/button/button';
import {Policy} from '../../../components/policy/policy';
import {setMissingClass} from '../../blog/subscribe/subscribe';
import st from './form.module.scss';
import developerImage from '../assets/developer.svg';
import managerImage from '../assets/project-manager.svg';
import designerImage from '../assets/designer.svg';
import analystImage from '../assets/analyst.svg';
import otherImage from '../assets/other.svg';

export enum ErrorTypes {
  Email = 'INVALID_EMAIL',
  Phone = 'NUMBER_OUT_OF_RANGE',
}
export const handleInput = fn => e => fn(e.target.value);

export const Form = ({professions}) => {
  const fileField = React.useRef<HTMLInputElement>(null);

  const images = [developerImage, managerImage, designerImage, analystImage, otherImage];
  const updatedProfessions = useMemo(
    () =>
      professions?.map((profession, index) => ({...profession, image: {...profession.image, src: images[index]}})) ||
      [],
    [],
  );

  function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(e.target.files);
    setCv(files[0]);
    if (missingInput.includes('cv')) {
      setMissingInput(missingInput.filter(i => i !== 'cv'));
    }
  }

  const node = useRef();
  useEffect(() => {
    typeof document !== `undefined` && document.addEventListener('mousedown', handleClick);

    return () => {
      typeof document !== `undefined` && document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const handleClick = e => {
    if (node.current.contains(e.target)) {
      return;
    }

    setOpenDropdown(false);
  };

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState({});
  const [openDropdown, setOpenDropdown] = useState(false);
  const [profession, setProffesion] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [hear, setHear] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [cv, setCv] = useState(undefined);
  const [consent, setConsent] = useState(false);
  const [missingInput, setMissingInput] = useState([]);

  const handleProfession = title => () => {
    setProffesion(title);
    setOpenDropdown(false);
  };

  const clearFields = () => {
    setFirstName('');
    setEmail('');
    setProffesion('');
    setLastName('');
    setHear('');
    setLinkedin('');
    setConsent(false);
    setCv(null);
  };

  const sendHubspot = async e => {
    if (e) e.preventDefault();
    const missingFields = [];
    if (!firstName) {
      missingFields.push('firstName');
    }
    if (!email) {
      missingFields.push('email');
    }
    if (!profession) {
      missingFields.push('profession');
    }
    if (!lastName) {
      missingFields.push('lastName');
    }
    if (!consent) {
      missingFields.push('consent');
    }

    if (missingFields.length > 0) {
      setMissingInput(missingFields);
      return;
    }

    setLoading(true);
    setSuccess(false);
    setError({});
    const isBrowser = typeof window !== 'undefined';
    const pageUri = isBrowser ? window.location.href : null;
    const pageName = isBrowser ? document.title : null;
    const postUrl = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${joinUsFormId}`;

    const body = {
      submittedAt: Date.now(),
      fields: [
        {
          name: 'profession_single',
          value: profession,
        },
        {
          name: 'email',
          value: email,
        },
        {
          name: 'firstname',
          value: firstName,
        },
        {
          name: 'lastname',
          value: lastName,
        },
        {
          name: 'where_did_you_hear_about_us_',
          value: hear,
        },
        {
          name: 'twitterhandle',
          value: linkedin,
        },
        {name: 'gdpr_consent', value: consent},
      ],
      context: {
        pageUri,
        pageName,
      },
      legalConsentOptions: {
        consent: {
          consentToProcess: true,
          text: `I acknowledge that the Blue House Technologies screening process is confidential and that I will not publicly disclose details about this process. By submitting, you acknowledge that you have read and agree to our Privacy Policy and Cookie Policy.`,
        },
      },
    };

    const payload = JSON.stringify(body);
    const xhr = new XMLHttpRequest();

    xhr.open('POST', postUrl);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onreadystatechange = function () {
      if (xhr.readyState == 4 && xhr.status == 200) {
        const filePostUrl = `${herokuUrl}/join-us-forms`;
        const fd = new FormData();
        cv && fd.append('files.CV', cv, cv.name);
        fd.append('data', JSON.stringify({Name: firstName, Last_name: lastName, Email: email}));
        Axios.post(filePostUrl, fd);
        setSuccess(true);
        clearFields();
        setMissingInput([]);
        setLoading(false);
        setError({});
        setSuccess(true);
      } else if (xhr.readyState == 4 && [400, 403, 404].includes(xhr.status)) {
        if (JSON.parse(xhr.response)?.errors?.map(error => error.errorType === ErrorTypes.Email)) {
          setError({email: 'Invalid email address.'});
        } else {
          setError({form: 'We had some issues with submission. Try again later.'});
        }
        setMissingInput([]);
        setLoading(false);
        setSuccess(false);
      }
    };

    //Set data for analitics

    if (isBrowser) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'joinusForm',
        profession: profession,
        buzzSource: hear,
      });
    }
    xhr.send(payload);
  };

  const handleDropdown = () => {
    setOpenDropdown(!openDropdown);
  };
  return (
    <form className={st.form}>
      <div className={st.input__wrapper}>
        <div className={st.mark}>*</div>
        <div
          className={`${st.dropdown__input} ${setMissingClass(missingInput, 'profession', st.required)} ${
            openDropdown ? st.active : ''
          }`}
          ref={node}
        >
          <div className={st.input__style}>
            <input placeholder={'Choose Profession'} readOnly={true} value={profession} onClick={handleDropdown} />
            <div className={st.chevron__container} onClick={handleDropdown}>
              <i className={st.chevron} />
            </div>
          </div>
          <div className={`${st.dropdown__field} ${openDropdown ? st.open : ''}`}>
            {updatedProfessions.map(profession => (
              <div key={profession.title} className={st.field} onClick={handleProfession(profession.title)}>
                <img className={st.image} src={profession.image.src} alt={profession.title} />
                <div className={st.title}>{profession.title}</div>
                <div className={st.description}>{profession.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={st.input__wrapper}>
        <div className={st.mark}>*</div>
        <div className={st.input__style}>
          <input
            className={`${setMissingClass(missingInput, 'email', st.required)} ${error?.email && st.required}`}
            placeholder={'E-mail Address'}
            value={email}
            onChange={handleInput(setEmail)}
          />
        </div>
      </div>
      <div className={st.input__wrapper}>
        <div className={st.mark}>*</div>
        <div className={st.input__style}>
          <input
            className={setMissingClass(missingInput, 'firstName', st.required)}
            placeholder={'First Name'}
            value={firstName}
            onChange={handleInput(setFirstName)}
          />
        </div>
      </div>
      <div className={st.input__wrapper}>
        <div className={st.mark}>*</div>
        <div className={st.input__style}>
          <input
            className={setMissingClass(missingInput, 'lastName', st.required)}
            placeholder={'Last Name'}
            value={lastName}
            onChange={handleInput(setLastName)}
          />
        </div>
      </div>
      <div className={st.input__wrapper}>
        <div className={st.input__style}>
          <input placeholder={'LinkedIn Account'} value={linkedin} onChange={handleInput(setLinkedin)} />
        </div>
      </div>

      <div className={st.input__wrapper}>
        <input
          id='file_cv'
          type='file'
          className={`${st.file__input} ${cv ? st.attached : ''}`}
          onChange={handleFileSelected}
          accept={'.doc,.docx,.pdf,.odt'}
          ref={fileField}
        />
        <div className={st.chevron__container__file}>
          <i className={st.chevron} />
        </div>
      </div>
      <div className={`${st.input__wrapper} ${st.checkbox__container}`}>
        <input
          type='checkbox'
          id='consent'
          onClick={() => setConsent(!consent)}
          checked={consent}
          onChange={() => {}}
        />
        <div className={`${st.checkmark} ${setMissingClass(missingInput, 'consent', st.required)}`}></div>
        <label htmlFor='consent' className={st.policy}>
          <Policy />
        </label>
      </div>
      <div className={st.wrapper}>
        <Button disabled={loading || success} stClassName={st.button} onClick={sendHubspot}>
          {success ? 'Submitted' : loading ? 'Sending' : 'Send Request*'}
        </Button>
        {success && <span className={st.additional__info}>{success && 'Thank you for your submission.'}</span>}
        {error.form && <span className={`${st.additional__info} ${st.error}`}>{error.form}</span>}
        {error.email && <span className={`${st.additional__info} ${st.error}`}>{error.email}</span>}
      </div>
    </form>
  );
};
